/**
 * @file        src/api/emailSettings.js
 * @description Per-brand SMTP Email Settings API calls (/api/email-settings).
 *              Never carries a raw/full SMTP password over the wire in either
 *              direction beyond a single explicit test/save request body — reads
 *              only ever get `smtp_pass_last4` back for display.
 * @author      Kinetika Labs
 * @created     2026-08-05
 * @changelog
 *   - 2026-10-09 Claude — verifyEmailSettings(): re-tests the SAVED config.
 *   - 2026-08-05 Claude — Initial (Email Settings admin UI).
 *   - 2026-08-05 Claude — Exported `unwrapList()` so the Topbar pill, the
 *                          Dashboard banner and the settings page itself share
 *                          one bare-array-or-`{data:[...]}` tolerant reader
 *                          instead of three copies of the same ternary.
 */
import { get, put, del, post } from './client';

/** List endpoints in this app are wrapped `{ data: [...] }`; tolerate a bare array too. */
export const unwrapList = (raw) => (Array.isArray(raw) ? raw : raw?.data || []);

/** Every configured row for the current tenant (brand rows + the null/"Default / Admin" row). */
export const listEmailSettings = () => get('/email-settings');

/** Lightweight per-brand connection status, for the topbar pill + post-login banner. */
export const getEmailSettingsStatus = () => get('/email-settings/status');

/** Live-tests an SMTP connection WITHOUT persisting anything. brandKey: 3-char code or 'default'. */
export const testEmailSettings = (brandKey, body) => post(`/email-settings/${brandKey}/test`, body);

/** Re-tests a brand's SAVED config (stored password, server-side) and records connected/failed. */
export const verifyEmailSettings = (brandKey) => post(`/email-settings/${brandKey}/verify`, {});

/** Saves a brand's (or the default's) SMTP settings. Re-authenticates via `body.password`. */
export const saveEmailSettings = (brandKey, body) => put(`/email-settings/${brandKey}`, body);

/** Removes a brand's (or the default's) SMTP settings. Re-authenticates via `body.password`. */
export const deleteEmailSettings = (brandKey, body) => del(`/email-settings/${brandKey}`, { data: body });
